import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import ResultsModal from './ResultsModal'

interface ChartData {
  name: string
  value: number
  [key: string]: string | number
}

interface TableData {
  id: number
  name: string
  status: string
  value: number
  [key: string]: any
}

interface ApiStats {
  total_parts_analyzed: number
  category_totals: {
    IGNORE: number
    CRITICAL: number
    CONSTRAINT: number
  }
  monthly_trend: Array<any>
  recent_analyses: Array<any>
}

interface HomeTabProps {
  chartData: ChartData[]
  pieData: Array<{ name: string; value: number }>
  tableData: TableData[]
  stats?: ApiStats | null
  loading?: boolean
}

// Use same color scheme as the bar chart for consistency: Critical, Constraint, Ignore
const COLORS = ['#e74c3c', '#f39c12', 'rgb(87, 130, 43)']

const HomeTab: React.FC<HomeTabProps> = ({ chartData, pieData, tableData, stats, loading }) => {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Navigate to job details page when a row is clicked
  const navigate = useNavigate()
  const handleRowClick = (jobId: string) => {
    navigate(`/job/${jobId}`)
  }

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <p>Loading stats...</p>
      </div>
    )
  }

  // Calculate stats values
  const totalParts = stats?.total_parts_analyzed || 0
  const criticalCount = stats?.category_totals.CRITICAL || 0
  const constraintCount = stats?.category_totals.CONSTRAINT || 0
  const ignoreCount = stats?.category_totals.IGNORE || 0

  return (
    <div>
      {/* Stats Block */}
      <div className="stats-block">
        <div className="stat-card">
          <div className="stat-label">Total Parts Analyzed</div>
          <div className="stat-value">{totalParts.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ignore</div>
          <div className="stat-value" style={{ color: 'rgb(87, 130, 43)' }}>{ignoreCount.toLocaleString()}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-label">Critical</div>
          <div className="stat-value" style={{ color: '#e74c3c' }}>{criticalCount.toLocaleString()}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-label">Constraint</div>
          <div className="stat-value" style={{ color: '#f39c12' }}>{constraintCount.toLocaleString()}</div>
        </div>
        
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Bar Chart */}
        <div className="chart-card">
          <h3>Shortage Trends Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="IGNORE" fill="rgb(87, 130, 43)" />
              <Bar dataKey="CRITICAL" fill="#e74c3c" />
              <Bar dataKey="CONSTRAINT" fill="#f39c12" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="chart-card">
          <h3>Category Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Jobs Table */}
      <div className="table-card">
        <h3>Recent Jobs</h3>
        <table>
          <thead>
            <tr>
              <th>Job ID</th>
              <th>Date</th>
              <th>Input File</th>
              <th>Parts</th>
              <th>Ignore</th>
              <th>Critical</th>
              <th>Constraint</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row.jobId || String(row.id))}
                style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
              >
                <td>{row.jobId || row.id}</td>
                <td>{row.date || 'N/A'}</td>
                <td>{row.name}</td>
                <td>{row.value}</td>
                <td>{row.ignore || 0}</td>
                <td>{row.critical || 0}</td>
                <td>{row.constraint || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Results Modal */}
      {selectedJobId && (
        <ResultsModal
          jobId={selectedJobId}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  )
}

export default HomeTab
